import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@heroui/input";
import { Button } from "@heroui/button";
import { XCircle, CheckCircle } from "lucide-react";

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidToken, setIsValidToken] = useState(true);

  // Get token from URL parameters
  const token = searchParams.get("token");

  // Validate token when component mounts
  useEffect(() => {
    // In a real application, you would validate the token with your backend
    if (!token) {
      setIsValidToken(false);
      setError(
        "Invalid or missing reset token. Please request a new password reset link.",
      );
    } else {
      // Simulated token validation - in reality, you would check this against your backend
      const isValid = token.length >= 20; // Just a simple check for demo

      setIsValidToken(isValid);

      if (!isValid) {
        setError(
          "Your password reset link has expired or is invalid. Please request a new one.",
        );
      }
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long");

      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");

      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Simulated API call
      // In a real app, you would send the token and new password to your backend
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Assume success for demo
      setSuccess(true);

      // Redirect to login after a delay
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: any) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-xl p-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Set New Password
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mt-2">
          Create a new password for your account
        </p>
      </div>

      {!isValidToken ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="h-8 w-8 text-red-500 dark:text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Invalid Reset Link
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <Link
            className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
            to="/forgot-password"
          >
            Request a new reset link
          </Link>
        </div>
      ) : success ? (
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-500 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Password Reset Successful
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Your password has been reset successfully. You'll be redirected to
            login shortly.
          </p>
          <Link
            className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
            to="/login"
          >
            Go to Login
          </Link>
        </div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <Input
              disabled={isSubmitting}
              label="New Password"
              placeholder="Enter your new password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Password must be at least 8 characters long
            </p>
          </div>

          <div>
            <Input
              disabled={isSubmitting}
              label="Confirm Password"
              placeholder="Confirm your new password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <Button
            fullWidth
            className="py-2"
            color="primary"
            isLoading={isSubmitting}
            type="submit"
          >
            Reset Password
          </Button>

          <div className="text-center">
            <Link
              className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
              to="/login"
            >
              Back to Login
            </Link>
          </div>
        </form>
      )}
    </div>
  );
};

export default ResetPasswordPage;
